import React, { useState } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useAuth } from '../contexts/AuthContext';

const Login = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, checkProfileStatus } = useAuth();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);
      if (result.success) {
        // Check profile status before redirecting
        const profileStatus = await checkProfileStatus();
        
        enqueueSnackbar('Login successful', { 
          variant: 'success',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });

        // Redirect based on profile status
        if (profileStatus === 'incomplete') {
          navigate('/profile');
        } else {
          navigate('/');
        }
      } else {
        console.log("result");
        console.log(result);
        const errorMessage = result.message || 'Login failed 3';
        enqueueSnackbar(errorMessage, { 
          variant: 'error',
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center'
          }
        });
      }
    } catch (error) {
      let errorMessage = 'An error occurred during login';
      
      if (error.response) {
        if (error.response.data && error.response.data.msg) {
          errorMessage = error.response.data.msg;
        } else if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.status === 401) {
          errorMessage = 'Invalid email or password';
        } else if (error.response.status === 403) {
          errorMessage = 'Account is inactive or blocked';
        } else if (error.response.status === 404) {
          errorMessage = 'User not found';
        }
      } else if (error.request) {
        errorMessage = 'No response from server. Please check your internet connection.';
      }

      enqueueSnackbar(errorMessage, { 
        variant: 'error',
        anchorOrigin: {
          vertical: 'top',
          horizontal: 'center'
        }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Box sx={{ mb: 4 }}>
          <img 
            src={`${process.env.REACT_APP_BASE_URL}/img/eworxs.png`}  
            alt="EWORXS Logo" 
            style={{ 
              width: '200px',
              height: 'auto'
            }} 
          />
        </Box>

        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>

          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 700,
              background: 'linear-gradient(135deg, #4e0159 0%, #4e0159 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              textAlign: 'center',
              mb: 3,
              letterSpacing: '-0.5px',
              fontSize: { xs: '1.75rem', sm: '2rem' },
              textShadow: '0 2px 4px rgba(78, 1, 89, 0.1)',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: '-8px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '60px',
                height: '3px',
                background: 'linear-gradient(90deg, #4e0159, #4e0159)',
                borderRadius: '2px'
              }
            }}
          >
            Login
          </Typography>

          <form onSubmit={handleSubmit}>
            <TextField
              required
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              margin="normal"
            />
            <TextField
              required
              fullWidth
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleChange}
              margin="normal"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Log In'}
            </Button>
            <Button
              fullWidth
              variant="text"
              onClick={() => navigate('/forgot-password')}
              sx={{
                color: '#4e0159',
                '&:hover': {
                  backgroundColor: 'rgba(78, 1, 89, 0.08)'
                }
              }}
            >
              Forgot Password?
            </Button>
          </form>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login; 